import React, { useEffect, useState } from 'react';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';

const MultilingualInput = ({ value = {}, defaultLang = 'fr', otherLang = 'en', onChange, placeholder = '', type = 'text', inputClass = '', textareaClass = '' }) => {
  const { t } = useTranslation();
  const [translations, setTranslations] = useState(() => ({
    [defaultLang]: (typeof value === 'object' ? (value[defaultLang] || '') : value || ''),
    [otherLang]: typeof value === 'object' ? (value[otherLang] || '') : '',
  }));

  const [showOther, setShowOther] = useState(() => Boolean(translations[otherLang]));

  // Track last emitted value to avoid echoing back parent updates and causing loops
  const lastEmittedRef = React.useRef(JSON.stringify(translations));

  // Sync incoming `value` prop into local state only when it truly differs
  useEffect(() => {
    const incoming = {
      [defaultLang]: (typeof value === 'object' ? (value[defaultLang] || '') : value || ''),
      [otherLang]: typeof value === 'object' ? (value[otherLang] || '') : '',
    };
    const incStr = JSON.stringify(incoming);
    if (incStr !== JSON.stringify(translations)) {
      setTranslations(incoming);
      setShowOther(Boolean(incoming[otherLang]));
      lastEmittedRef.current = incStr;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  // Emit changes to parent, but guard against emitting the same payload repeatedly
  useEffect(() => {
    const str = JSON.stringify(translations);
    if (onChange && lastEmittedRef.current !== str) {
      onChange({ ...translations });
      lastEmittedRef.current = str;
    }
  }, [translations, onChange, defaultLang, otherLang]);

  const setField = (lang, v) => setTranslations(prev => ({ ...prev, [lang]: v }));

  return (
    <div>
      <div>
  <label style={{ display: 'block', fontSize: 12 }}>{t(`languages.${defaultLang}`, { defaultValue: defaultLang.toUpperCase() })}</label>
        {type === 'textarea' ? (
          <textarea className={textareaClass} value={translations[defaultLang]} placeholder={placeholder} onChange={e => setField(defaultLang, e.target.value)} />
        ) : (
          <input className={inputClass} type="text" value={translations[defaultLang]} placeholder={placeholder} onChange={e => setField(defaultLang, e.target.value)} />
        )}
      </div>

      <div
        style={{ display: 'flex', alignItems: 'center', marginTop: 8, cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onClick={() => setShowOther(s => !s)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowOther(s => !s); } }}
      >
        <Switch
          checked={showOther}
          onChange={(e, checked) => { e.stopPropagation(); setShowOther(checked); }}
          inputProps={{ 'aria-label': `${otherLang}-toggle` }}
        />
        <label style={{ marginLeft: 8, userSelect: 'none' }}>
          {t('form.translation', 'Translation')} {t(`languages.${otherLang}`, { defaultValue: otherLang.toUpperCase() })}
        </label>
      </div>

      {showOther && (
        <div style={{ marginTop: 8 }}>
          <label style={{ display: 'block', fontSize: 12 }}>{t(`languages.${otherLang}`, { defaultValue: otherLang.toUpperCase() })}</label>
          {type === 'textarea' ? (
            <textarea className={textareaClass} value={translations[otherLang]} placeholder={placeholder} onChange={e => setField(otherLang, e.target.value)} />
          ) : (
            <input className={inputClass} type="text" value={translations[otherLang]} placeholder={placeholder} onChange={e => setField(otherLang, e.target.value)} />
          )}
        </div>
      )}
    </div>
  );
};

export default MultilingualInput;
